import React, { useMemo } from 'react';
import { formatLapSeconds } from '../../analytics/lapTime';
import {
  buildPath,
  downsample,
  extent,
  linearScale,
  niceTicks,
  padDomain,
  Point,
  seriesColour,
} from './chartUtils';
import './charts.css';

export interface LapSeries {
  teamId: string;
  label: string;
  kartNumber: string;
  /** Laps per stint, oldest first — stint breaks are drawn between them. */
  stints: number[][];
}

interface LapTraceChartProps {
  series: LapSeries[];
  height?: number;
  /** Point budget per series before the trace is thinned. */
  maxPointsPerSeries?: number;
}

const MARGIN = { top: 8, right: 8, bottom: 18, left: 44 };
const VIEW_WIDTH = 720;

/**
 * Lap times against lap number for one or more teams.
 *
 * The y domain is trimmed to the middle of the distribution rather than the full
 * range: a single in-lap is often 40 seconds slower than green pace, and scaling
 * to it flattens every real difference into a straight line.
 */
export function trimmedDomain(values: number[]): [number, number] | null {
  const span = extent(values);
  if (!span) return null;
  if (values.length < 8) return span;

  // A band around the median rather than a percentile: percentiles need a large
  // sample to exclude anything at all — the 95th of ten laps is still the worst
  // of them — whereas the band holds however few laps there are.
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  if (!(median > 0)) return span;

  const lo = Math.max(span[0], median * 0.94);
  const hi = Math.min(span[1], median * 1.1);
  return hi > lo ? [lo, hi] : span;
}

export const LapTraceChart: React.FC<LapTraceChartProps> = ({
  series,
  height = 220,
  maxPointsPerSeries = 400,
}) => {
  const model = useMemo(() => {
    const flatSeries = series.map((s) => ({
      ...s,
      laps: s.stints.flat(),
      // Lap number where each stint ends, for the divider lines.
      breaks: s.stints.reduce<number[]>((acc, stint) => {
        const prev = acc.length ? acc[acc.length - 1] : 0;
        acc.push(prev + stint.length);
        return acc;
      }, []),
    }));

    const allLaps = flatSeries.flatMap((s) => s.laps);
    if (allLaps.length < 2) return null;

    const yDomain = trimmedDomain(allLaps);
    if (!yDomain) return null;

    const maxLaps = Math.max(...flatSeries.map((s) => s.laps.length));
    const x = linearScale([1, Math.max(maxLaps, 2)], [MARGIN.left, VIEW_WIDTH - MARGIN.right]);
    const y = linearScale(padDomain(yDomain, 0.06), [height - MARGIN.bottom, MARGIN.top]);

    const paths = flatSeries.map((s) => {
      const points: Point[] = s.laps.map((seconds, i) => ({
        x: x(i + 1),
        // Clamp to the frame so a trimmed outlier stays visible at the edge
        // rather than being drawn off-chart.
        y: Math.max(MARGIN.top, Math.min(height - MARGIN.bottom, y(seconds))),
      }));
      return {
        teamId: s.teamId,
        label: s.label,
        kartNumber: s.kartNumber,
        d: buildPath(downsample(points, maxPointsPerSeries)),
        breaks: s.breaks
          .slice(0, -1)
          .filter((b) => b > 0)
          .map((b) => x(b + 0.5)),
      };
    });

    return {
      paths,
      yTicks: niceTicks(yDomain[0], yDomain[1], 4).map((v) => ({ v, y: y(v) })),
      xTicks: niceTicks(1, maxLaps, 6).map((v) => ({ v, x: x(v) })),
    };
  }, [series, height, maxPointsPerSeries]);

  if (!model) {
    return <div className="chart-empty">Not enough laps yet to draw a trace.</div>;
  }

  return (
    <>
      <div className="chart-legend">
        {model.paths.map((p, i) => (
          <span key={p.teamId} className="chart-legend-item">
            <span
              className="chart-swatch"
              style={{ background: seriesColour(i) }}
              aria-hidden
            />
            #{p.kartNumber} {p.label}
          </span>
        ))}
      </div>

      <svg
        className="chart"
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Lap times by lap number"
      >
        {model.yTicks.map((t) => (
          <g key={`y${t.v}`}>
            <line
              className="chart-grid-line"
              x1={MARGIN.left}
              x2={VIEW_WIDTH - MARGIN.right}
              y1={t.y}
              y2={t.y}
            />
            <text className="chart-axis-label" x={MARGIN.left - 4} y={t.y + 3} textAnchor="end">
              {formatLapSeconds(t.v)}
            </text>
          </g>
        ))}

        {model.xTicks.map((t) => (
          <text
            key={`x${t.v}`}
            className="chart-axis-label"
            x={t.x}
            y={height - 5}
            textAnchor="middle"
          >
            {t.v}
          </text>
        ))}

        {model.paths.map((p, i) => (
          <g key={p.teamId}>
            {p.breaks.map((bx, bi) => (
              <line
                key={bi}
                className="chart-stint-boundary"
                x1={bx}
                x2={bx}
                y1={MARGIN.top}
                y2={height - MARGIN.bottom}
              />
            ))}
            <path className="chart-series" d={p.d} style={{ stroke: seriesColour(i) }} />
          </g>
        ))}
      </svg>
    </>
  );
};

export default LapTraceChart;
